import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

export const PAGE_SIZES = [10, 25, 50, 100] as const

interface Props {
  page: number
  pages: number
  pageSize: number
  /** Total rows across all pages. */
  count: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

/**
 * Page numbers with ellipses: first, last, and a window around the current
 * page. Rendering every number breaks the layout as soon as a season's worth
 * of rewards is on screen.
 */
export function pageWindow(page: number, pages: number): Array<number | '…'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)

  const out: Array<number | '…'> = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(pages - 1, page + 1)

  if (start > 2) out.push('…')
  for (let i = start; i <= end; i += 1) out.push(i)
  if (end < pages - 1) out.push('…')
  out.push(pages)
  return out
}

export default function Pagination({
  page,
  pages,
  pageSize,
  count,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const { t } = useTranslation()
  if (count === 0) return null

  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, count)

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-200 px-2.5 py-2">
      <span className="text-[12px] text-slate-500">
        {t('global.pageRange', { first, last, count })}
      </span>

      <label className="flex items-center gap-1.5 text-[12px] text-slate-500">
        {t('global.perPage')}
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number.parseInt(e.target.value, 10))}
          className="input w-auto py-0.5 text-[12px]"
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      {pages > 1 ? (
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="btn-secondary px-1.5 py-0.5"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label={t('global.previousPage')}
            title={t('global.previousPage')}
          >
            <ChevronLeft size={15} />
          </button>

          {pageWindow(page, pages).map((entry, index) =>
            entry === '…' ? (
              <span key={`gap-${index}`} className="px-1 text-[12px] text-slate-400">
                …
              </span>
            ) : (
              <button
                key={entry}
                type="button"
                onClick={() => onPageChange(entry)}
                className={cn(
                  'min-w-[1.75rem] rounded border px-1.5 py-0.5 text-[12px]',
                  entry === page
                    ? 'border-brand-500 bg-brand-50 font-medium text-brand-700'
                    : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {entry}
              </button>
            ),
          )}

          <button
            type="button"
            className="btn-secondary px-1.5 py-0.5"
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            aria-label={t('global.nextPage')}
            title={t('global.nextPage')}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      ) : null}
    </div>
  )
}
